/* ── Device tracking utility ──
 * Detects the current device from User-Agent.
 * Phase 1 only collects the current device fingerprint for auth/security risk signals.
 * It does not provide multi-device management or a device registry API.
 */

export interface TrackedDevice {
  id: string;
  name: string;
  os: string;
  browser: string;
  icon: string;
  lastActive: string; // ISO timestamp
  firstSeen: string;  // ISO timestamp
}

/* ── UA parser ── */
function parseUA(): Omit<TrackedDevice, 'id' | 'lastActive' | 'firstSeen'> {
  const ua = navigator.userAgent;

  // OS detection
  let os = 'Unknown';
  if (/Windows NT 10/.test(ua)) os = 'Windows 10/11';
  else if (/Windows NT 6/.test(ua)) os = 'Windows 7/8';
  else if (/Mac OS X 10[._]15/.test(ua)) os = 'macOS Catalina';
  else if (/Mac OS X 10[._]14/.test(ua)) os = 'macOS Mojave';
  else if (/Mac OS X/.test(ua)) {
    const m = ua.match(/Mac OS X ([0-9_]+)/);
    os = m ? `macOS ${m[1].replace(/_/g, '.')}` : 'macOS';
  }
  else if (/iPhone OS 17/.test(ua)) os = 'iOS 17';
  else if (/iPhone OS 18/.test(ua)) os = 'iOS 18';
  else if (/iPhone OS/.test(ua)) {
    const m = ua.match(/iPhone OS ([0-9_]+)/);
    os = m ? `iOS ${m[1].replace('_', '.')}` : 'iOS';
  }
  else if (/iPad/.test(ua)) os = 'iPadOS';
  else if (/Android 14/.test(ua)) os = 'Android 14';
  else if (/Android 15/.test(ua)) os = 'Android 15';
  else if (/Android/.test(ua)) {
    const m = ua.match(/Android ([0-9.]+)/);
    os = m ? `Android ${m[1]}` : 'Android';
  }
  else if (/Linux/.test(ua)) os = 'Linux';

  // Browser detection
  let browser = 'Unknown';
  if (/Edg\//.test(ua)) {
    const m = ua.match(/Edg\/([0-9]+)/);
    browser = m ? `Edge ${m[1]}` : 'Edge';
  } else if (/Chrome/.test(ua)) {
    const m = ua.match(/Chrome\/([0-9]+)/);
    browser = m ? `Chrome ${m[1]}` : 'Chrome';
  } else if (/Safari/.test(ua) && !/Chrome/.test(ua)) {
    const m = ua.match(/Version\/([0-9]+)/);
    browser = m ? `Safari ${m[1]}` : 'Safari';
  } else if (/Firefox/.test(ua)) {
    const m = ua.match(/Firefox\/([0-9]+)/);
    browser = m ? `Firefox ${m[1]}` : 'Firefox';
  }

  // Device name
  let name = '';
  let icon = 'ri-computer-line';
  if (/iPhone/.test(ua)) {
    name = /iPhone 15/.test(ua) ? 'iPhone 15 Pro' : /iPhone 14/.test(ua) ? 'iPhone 14' : 'iPhone';
    icon = 'ri-smartphone-line';
  } else if (/iPad/.test(ua)) {
    name = /iPad Pro/.test(ua) ? 'iPad Pro' : 'iPad';
    icon = 'ri-tablet-line';
  } else if (/Android/.test(ua)) {
    if (/Mobile/.test(ua)) {
      const m = ua.match(/([A-Z][a-z]+ [A-Z]?[0-9]+)/);
      name = m ? m[1] : 'Android 手机';
      icon = 'ri-smartphone-line';
    } else {
      name = 'Android 平板';
      icon = 'ri-tablet-line';
    }
  } else if (/Macintosh/.test(ua) || /Mac OS X/.test(ua)) {
    name = 'Mac';
    icon = 'ri-mac-line';
  } else if (/Windows/.test(ua)) {
    name = 'Windows PC';
    icon = 'ri-windows-fill';
  } else if (/Linux/.test(ua) && !/Android/.test(ua)) {
    name = 'Linux 桌面';
    icon = 'ri-computer-line';
  } else {
    name = browser;
    icon = 'ri-computer-line';
  }

  return { name, os, browser, icon };
}

/* ── Device fingerprint ── */
function generateFingerprint(): string {
  const parts = [
    navigator.userAgent,
    `${screen.width}x${screen.height}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.language,
  ];
  // Simple hash from concatenated string
  let hash = 0;
  const str = parts.join('|');
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0; // Convert to 32bit integer
  }
  return `dev_${Math.abs(hash).toString(36)}`;
}

/* ── Public API ── */

/** Record current device and return the current-device snapshot. */
export function recordCurrentDevice(): TrackedDevice[] {
  const now = new Date().toISOString();
  const fingerprint = generateFingerprint();
  const parsed = parseUA();

  return [{
    id: fingerprint,
    name: parsed.name,
    os: parsed.os,
    browser: parsed.browser,
    icon: parsed.icon,
    lastActive: now,
    firstSeen: now,
  }];
}

/** Get the current-device snapshot without retaining device history. */
export function getDevices(): TrackedDevice[] {
  return recordCurrentDevice();
}

/** Phase 1 does not support multi-device management; this is a no-op compatibility helper. */
export function removeDevice(deviceId: string): TrackedDevice[] {
  void deviceId;
  return recordCurrentDevice();
}

/** Get current device fingerprint */
export function getCurrentFingerprint(): string {
  return generateFingerprint();
}

/** Format relative time from ISO string */
export function formatRelativeTime(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return '刚刚';
  if (diff < 3600) return `${Math.floor(diff / 60)}分钟前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}小时前`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}天前`;
  return new Date(iso).toLocaleDateString('zh-CN');
}
